/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, DoCheck, OnInit, ViewEncapsulation} from '@angular/core';
import {FooterComponent, HeaderComponent} from '../templates';
import {NlrHasPermissionsPipe} from '../common/pipe/nlr-has-permissions.pipe';
import {NlrFormFieldHasErrorPipe} from '../common/pipe/nlr-form-field-has-error.pipe';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ExtractCadastreService} from './extract-cadastre.service';
import {
    ExtractCadastreParcelSearchRequest,
    ExtractCadastreParcelSearchResponse,
    ExtractCadastreSelectableParcelSearchResult
} from './extract-cadastre.model';
import {PATTERNS} from '../common/constants';
import {NlrPatternDirective} from '../common/directive/nlr-pattern.directive';
import {NgEventBus} from 'ng-event-bus';
import {NlrUppercaseDirective} from '../common/directive/nlr-uppercase.directive';
import {LookupService} from '../common/service/lookup.service';
import {ProjectedCoordSystemLookup} from '../common/models/projected-coord-system.model';
import {equals} from '../common/service/data-util';

@Component({
    selector: 'nlr-extract-cadastre',
    standalone: true,
    templateUrl: './extract-cadastre.component.html',
    styleUrls: ['./extract-cadastre.component.scss'],
    imports: [
        HeaderComponent,
        NlrHasPermissionsPipe,
        FooterComponent,
        NlrFormFieldHasErrorPipe,
        ReactiveFormsModule,
        FormsModule,
        NlrPatternDirective,
        NlrUppercaseDirective
    ],
    encapsulation: ViewEncapsulation.None
})
export class ExtractCadastreComponent implements OnInit, DoCheck {
    readonly MAX_PARCEL_SELECTION_COUNT = 10;

    title = 'ExtractCadastreComponent';

    landPattern = PATTERNS.LAND_PATTERN;

    parcelPattern = PATTERNS.PARCEL_PATTERN;

    surveyPattern = PATTERNS.SURVEY_PATTERN;

    titlePattern = PATTERNS.TITLE_PATTERN;

    landIdentifier: string = '';

    parcelIdentifier: string = '';

    planIdentifier: string = '';

    titleIdentifier: string = '';

    parcelSearchResults: ExtractCadastreSelectableParcelSearchResult[];

    parcelSearchErrorMessage: string;

    projectedCoordinateSystemLookups: ProjectedCoordSystemLookup[] = [];

    selectedCoordinateSystem: ProjectedCoordSystemLookup = null;

    createExtractErrorMessage: string;

    extractDownloadUrl = '';

    extractDownloadUrlAtLastCheck = '';

    clearExtractDownloadUrlTimeout: number;

    readonly clearExtractDownloadUrlTimeoutMilliseconds = 15 * 60 * 1000;

    constructor(
        private eventBus: NgEventBus,
        private extractCadastreservice: ExtractCadastreService,
        private lookupService: LookupService
    ) {}

    ngOnInit() {
        this.projectedCoordinateSystemLookups = this.lookupService
            .getLookups('projectedCoordSystem')
            .filter((l: ProjectedCoordSystemLookup) => l.validPdfCreationMethods.includes('DIGITAL_PLAN'))
            .sort((a: ProjectedCoordSystemLookup, b: ProjectedCoordSystemLookup) => a.uiOptionOrder - b.uiOptionOrder);
    }

    ngDoCheck() {
        if (this.extractDownloadUrlAtLastCheck !== this.extractDownloadUrl) {
            this.extractDownloadUrlAtLastCheck = this.extractDownloadUrl;

            // Download url has changed so clear the timeout to clear the download url
            if (this.clearExtractDownloadUrlTimeout) {
                window.clearTimeout(this.clearExtractDownloadUrlTimeout);
                this.clearExtractDownloadUrlTimeout = null;
            }

            // New download url is present so set a new timeout to clear the download url
            if (this.extractDownloadUrl) {
                this.clearExtractDownloadUrlTimeout = window.setTimeout(() => {
                    console.debug(`${this.title} - clearing download url after timeout`);
                    this.extractDownloadUrl = '';
                }, this.clearExtractDownloadUrlTimeoutMilliseconds);
            }
        }
    }

    searchByParcelIdentifierDisabled() {
        return !this.parcelIdentifier || !this.parcelPattern.test(this.parcelIdentifier);
    }

    searchByParcelIdentifier() {
        console.debug(`${this.title} - searchByParcelIdentifier`);

        if (this.searchByParcelIdentifierDisabled()) {
            return;
        }
        this.search({parcelIdentifier: this.parcelIdentifier});
    }

    searchByLandIdentifierDisabled() {
        return !this.landIdentifier || !this.landPattern.test(this.landIdentifier);
    }

    searchByLandIdentifier() {
        console.debug(`${this.title} - searchByLandIdentifier`);

        if (this.searchByLandIdentifierDisabled()) {
            return;
        }
        this.search({landIdentifier: this.landIdentifier});
    }

    searchByTitleIdentifierDisabled() {
        return !this.titleIdentifier || !this.titlePattern.test(this.titleIdentifier);
    }

    searchByTitleIdentifier() {
        console.debug(`${this.title} - searchByTitleIdentifier`);

        if (this.searchByTitleIdentifierDisabled()) {
            return;
        }
        this.search({titleIdentifier: this.titleIdentifier});
    }

    searchByPlanIdentifierDisabled() {
        return !this.planIdentifier || !this.surveyPattern.test(this.planIdentifier);
    }

    searchByPlanIdentifier() {
        console.debug(`${this.title} - searchByPlanIdentifier`);

        if (this.searchByPlanIdentifierDisabled()) {
            return;
        }
        this.search({planIdentifier: this.planIdentifier});
    }

    isParcelSelectionDisabled(selectableParcelSearchResult: ExtractCadastreSelectableParcelSearchResult) {
        return !selectableParcelSearchResult.selected && this.getSelectedSearchResults().length >= this.MAX_PARCEL_SELECTION_COUNT;
    }

    coordinateSystemDisabled() {
        return !this.getSelectedSearchResults().length;
    }

    createExtractDisabled() {
        return !this.selectedCoordinateSystem;
    }

    createExtract() {
        console.debug(`${this.title} - createExtract`);

        this.createExtractErrorMessage = '';

        this.extractDownloadUrl = '';

        const subscription = this.extractCadastreservice
            .createExtract({
                landIdentifiers: this.getSelectedLandIdentifiers(),
                coordinateSystem: this.selectedCoordinateSystem.wkid
            })
            .subscribe({
                next: value => {
                    this.extractDownloadUrl = value.downloadUrl;
                },
                error: err => {
                    console.error(`${this.title} - createExtract - error:`, err);
                    this.createExtractErrorMessage = err.error;
                }
            });

        this.eventBus.cast('nlr:busy', subscription);
    }

    reset() {
        this.landIdentifier = '';
        this.parcelIdentifier = '';
        this.planIdentifier = '';
        this.titleIdentifier = '';

        this.parcelSearchErrorMessage = '';
        this.selectedCoordinateSystem = null;
        this.createExtractErrorMessage = '';
        this.extractDownloadUrl = '';

        if (this.parcelSearchResults) {
            this.parcelSearchResults.length = 0;
        }
    }

    resetDisabled() {
        return this.parcelSearchResults?.length === 0;
    }

    getSelectedParcelSummary() {
        const selectedParcelNames = this.getSelectedSearchResults().map(result => result.displayName);
        return `Selected: ${selectedParcelNames.length ? selectedParcelNames.join(', ') : 'none'}`;
    }

    private getSelectedSearchResults() {
        return this.parcelSearchResults ? this.parcelSearchResults.filter(result => result.selected).map(result => result.result) : [];
    }

    private getSelectedLandIdentifiers() {
        return this.getSelectedSearchResults().map(result => result.landIdentifier);
    }

    private search(searchRequest: ExtractCadastreParcelSearchRequest) {
        console.debug(`${this.title} - search`);

        this.parcelSearchErrorMessage = '';

        this.selectedCoordinateSystem = null;

        this.createExtractErrorMessage = '';

        this.extractDownloadUrl = '';

        // Clear other identifier fields if they're not part of the search request
        if (!(searchRequest as any).landIdentifier) {
            this.landIdentifier = '';
        }

        if (!(searchRequest as any).parcelIdentifier) {
            this.parcelIdentifier = '';
        }

        if (!(searchRequest as any).planIdentifier) {
            this.planIdentifier = '';
        }

        if (!(searchRequest as any).titleIdentifier) {
            this.titleIdentifier = '';
        }

        const subscription = this.extractCadastreservice.search(searchRequest).subscribe({
            next: (response: ExtractCadastreParcelSearchResponse) => {
                const searchResults = response.resultList;
                if (searchResults.length === 0) {
                    this.parcelSearchErrorMessage = 'No records found.';
                } else {
                    if (!this.parcelSearchResults) {
                        this.parcelSearchResults = [];
                    }
                    // Filter out parcels already in existing search results
                    const filteredSearchResults = searchResults.filter(
                        result => !this.parcelSearchResults.some(parcelSearchResult => equals(parcelSearchResult.result, result))
                    );
                    this.parcelSearchResults.push(...filteredSearchResults.map(result => ({result: result, selected: false})));
                    if (this.parcelSearchResults.length === 1) {
                        this.parcelSearchResults[0].selected = true;
                    }
                }
            },
            error: err => {
                console.error(`${this.title} - search - error:`, err);
                this.parcelSearchErrorMessage = err.error;
            }
        });

        this.eventBus.cast('nlr:busy', subscription);
    }
}
